import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Minus, Plus } from "lucide-react";
import { Button } from "@themes/pollinator/ui/Button";
import { Loader } from "@themes/pollinator/ui/Loader";
import { Type } from "@themes/pollinator/ui/Type";
import { marketingType } from "../../marketing.type";
import { mountNetworkGraph, type NetworkGraphHandle } from "./networkGraph.controller";
import { makeNetworkGraph, type NetworkGraphData } from "./networkGraph.model";

export type Props = {
  /** What the backend sent; the graph is drawn only if it says it is enabled */
  data?: NetworkGraphData;
  /** Whether the backend's answer is still on its way */
  isLoading?: boolean;
  /** Whether to cut between views instead of travelling between them */
  isMotionReduced?: boolean;
  /** How many links out from a chosen organization its neighbourhood reaches */
  hops?: number;
  className?: string;
};

/**
 * The network, to explore: organizations as named circles, the people who join
 * them as anonymous dots.
 *
 * Choosing an organization brings it to the centre, gathers its people round
 * it and rings it with the organizations it shares people with. The drawing
 * itself is a canvas run by `networkGraph.controller`; this component gives it
 * a frame, loads it only once the reader has scrolled near it, and covers the
 * wait with the loader. When there is no graph to draw, because staff have
 * switched it off or it could not be fetched or drawn, there is nothing here
 * at all.
 *
 * The page scrolls past the graph, so the wheel is left to the page and the
 * frame carries its own buttons for zooming in and out.
 *
 * A canvas says nothing to a screen reader, so the frame carries a sentence
 * describing what is in it and how to move through it by keyboard.
 */
export function MarketingNetworkGraph({
  data,
  isLoading = false,
  isMotionReduced = false,
  hops,
  className,
}: Props) {
  const frameRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const hoverLabelRef = useRef<HTMLDivElement>(null);
  const [isNear, setIsNear] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isEngaged, setIsEngaged] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const [zoomRange, setZoomRange] = useState({ canZoomIn: true, canZoomOut: true });
  const handleRef = useRef<NetworkGraphHandle | null>(null);

  const isDrawable = Boolean(data?.enabled && data.organizations?.length);

  // The library and the simulation are not small. Neither is fetched or run
  // until the frame is within a screen or so of being seen.
  useEffect(() => {
    const frame = frameRef.current;
    if (!isDrawable || !frame) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsNear(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsNear(true);
          observer.disconnect();
        }
      },
      { rootMargin: "600px 0px" }
    );
    observer.observe(frame);
    return () => observer.disconnect();
  }, [isDrawable]);

  useEffect(() => {
    if (!isDrawable || !isNear || !data) return;
    const frame = frameRef.current;
    const host = hostRef.current;
    const hoverLabel = hoverLabelRef.current;
    if (!frame || !host || !hoverLabel) return;

    let handle: NetworkGraphHandle | null = null;
    let isCancelled = false;
    mountNetworkGraph({
      host,
      frame,
      hoverLabel,
      graph: makeNetworkGraph(data),
      isMotionReduced,
      hops,
      onEngagedChange: setIsEngaged,
      onZoomRangeChange: setZoomRange,
      onReady: () => setIsReady(true),
    })
      .then((mounted) => {
        if (isCancelled) {
          mounted.destroy();
          return;
        }
        handle = mounted;
        handleRef.current = mounted;
      })
      .catch(() => {
        if (!isCancelled) setHasFailed(true);
      });

    return () => {
      isCancelled = true;
      handle?.destroy();
      handleRef.current = null;
      setIsReady(false);
      setIsEngaged(false);
    };
  }, [isDrawable, isNear, data, isMotionReduced, hops]);

  // Nothing stands in for the graph. While the answer is on its way the frame
  // holds its place, so the page does not jump when the graph arrives.
  if (hasFailed || (!isDrawable && !isLoading)) return null;

  // The words above and below the graph are said here rather than beside it in
  // the page, so that both appear only when the graph does: staff can switch it
  // off, and then this returns nothing and takes them with it.
  return (
    <div className="flex w-full flex-col gap-4 lg:gap-6">
      <Type
        variant="body1"
        as="h3"
        className={`${marketingType.body} font-bold max-w-none`}
      >
        Click the map below to explore the growing Pollinator network
        {/* Decoration: the words already say where the map is. */}
        <span aria-hidden="true">→</span>
      </Type>
      <div
        ref={frameRef}
        data-testid="marketing-network-graph"
        data-engaged={isEngaged ? "true" : "false"}
        role="group"
        aria-label="Pollinator network of organizations connected through anonymous participants"
        // Square on a phone, where a wide strip would leave the graph too small
        // to touch; the proportions of the picture it replaces everywhere else.
        // Merged, not joined: a caller that widens the graph past its column, as
        // the homepage does on a phone to run it edge to edge, has to be able to
        // replace the width set here.
        className={twMerge(
          "relative w-full aspect-square overflow-hidden rounded-[36px] bg-brand-50 md:aspect-[1248/677]",
          className
        )}
      >
        <Type variant="caption" as="p" className="sr-only">
          Select an organization to bring its relationships into focus. Select
          empty space, or press Escape, to return to the whole network.
        </Type>
        <div
          ref={hostRef}
          className={`h-full w-full transition-opacity duration-[600ms] ${
            isReady ? "opacity-100" : "opacity-0"
          }`}
        />
        <div
          ref={hoverLabelRef}
          hidden
          aria-hidden="true"
          className="pointer-events-none absolute z-10 max-w-[min(250px,70vw)] bg-brand-50 px-2 py-1.5 text-[13px] font-medium leading-[1.3] text-brand-800"
        />
        {isReady && (
          <div className="absolute bottom-4 right-4 z-10 flex flex-col gap-2 md:bottom-6 md:right-6">
            <Button
              variant="secondary"
              aria-label="Zoom in"
              iconComponent={Plus}
              disabled={!zoomRange.canZoomIn}
              onClick={() => handleRef.current?.zoomIn()}
              className="h-10 w-10 rounded-full bg-brand-50 p-0 disabled:bg-brand-50 disabled:opacity-40"
              data-testid="marketing-network-graph-zoom-in"
            />
            <Button
              variant="secondary"
              aria-label="Zoom out"
              iconComponent={Minus}
              disabled={!zoomRange.canZoomOut}
              onClick={() => handleRef.current?.zoomOut()}
              className="h-10 w-10 rounded-full bg-brand-50 p-0 disabled:bg-brand-50 disabled:opacity-40"
              data-testid="marketing-network-graph-zoom-out"
            />
          </div>
        )}
        {!isReady && (
          <div
            className="absolute inset-0 flex items-center justify-center"
            data-testid="marketing-network-graph-loading"
          >
            <Loader innerClassName="h-14 w-14" />
          </div>
        )}
      </div>
      <Type variant="body2" className={`${marketingType.small} italic max-w-none`}>
        This interactive diagram maps the growing Pollinator network in real
        time. Organizations are linked when they share alumni, and when their
        alumni are connected through our peer-coaching program. Note that the
        tiny dots represent actual people, and the larger dots represent
        partner organizations.
      </Type>
    </div>
  );
}

export default MarketingNetworkGraph;
